/**
 * lazyWithRetry - Wrapper around React.lazy that handles chunk load failures
 *
 * After a deployment, cached HTML may reference old chunk filenames that no longer exist.
 * This utility catches the "Failed to fetch dynamically imported module" error and
 * automatically reloads the page once to get fresh assets.
 */
import React, { ComponentType } from 'react';

const RELOAD_KEY = 'chunk_reload_attempted';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

/**
 * Safely interact with sessionStorage, handling cases where it's unavailable
 * (private browsing, storage disabled, quota exceeded, etc.)
 */
function getSessionFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === 'true';
  } catch {
    // Storage unavailable - treat as "already attempted" to prevent reload loops
    return true;
  }
}

function setSessionFlag(key: string, value: boolean): void {
  try {
    if (value) {
      sessionStorage.setItem(key, 'true');
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable - silently ignore
  }
}

/**
 * Check if an error is a chunk/module load failure.
 * Covers Vite, Webpack, and common browser error patterns.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  const name = error.name;

  return (
    // Vite dynamic import failures
    message.includes('failed to fetch dynamically imported module') ||
    // Webpack chunk loading failures
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    name === 'ChunkLoadError' ||
    // Network failures that could indicate stale chunks
    (message.includes('failed to fetch') && message.includes('module')) ||
    // Some bundlers use this pattern
    message.includes('error loading dynamically imported module')
  );
}

/**
 * Wrapper around React.lazy that automatically reloads the page on chunk load failure.
 * Uses sessionStorage to prevent infinite reload loops.
 *
 * Handles edge cases:
 * - sessionStorage unavailable (private browsing, disabled storage)
 * - Various bundler error message formats (Vite, Webpack)
 * - Prevents infinite reload loops even if storage fails
 */
export function lazyWithRetry<T extends AnyComponent>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    // Check if we've already attempted a reload this session
    const hasReloaded = getSessionFlag(RELOAD_KEY);

    try {
      const module = await importFn();
      // Success - clear the reload flag only if it was previously set
      if (hasReloaded) {
        setSessionFlag(RELOAD_KEY, false);
      }
      return module;
    } catch (error) {
      if (isChunkLoadError(error) && !hasReloaded) {
        // Set flag to prevent infinite reload loop
        setSessionFlag(RELOAD_KEY, true);
        // Reload the page to get fresh assets
        window.location.reload();
        // Return a never-resolving promise since we're reloading
        return new Promise(() => {});
      }

      // Either not a chunk error or we already tried reloading - rethrow
      throw error;
    }
  });
}

